import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ConfigInterface } from '../../interfaces/config.interface';
import { EventService } from '../event/event.service';
import { Store } from '@ngrx/store';
import { initConfig } from '../../state/config/actions/config.actions';
import * as ConfigSelectors from '../../state/config/selectors/config.selector';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { LoadStatus } from '../../state/load-status';

@Injectable({
    providedIn: 'root',
})
export class ConfigService implements ConfigInterface, InitializableInterface {
    public get isInitialized(): boolean {
        return this.store.selectSignal(ConfigSelectors.selectConfigLoaded)();
    }

    public constructor(
        private readonly eventService: EventService,
        private readonly store: Store,
    ) {
        this.eventService.syncReceiveCompleted$.subscribe(() => this.init());
    }

    /**
     * Trigger the load effect and resolve once the slice settles: complete on success, or error
     * on failure. Rehydration from the offline cache is also triggered by the dispatched action,
     * so cached config is available while the backend request is in flight.
     */
    public init(): Observable<void> {
        this.store.dispatch(initConfig());
        return this.store.select(ConfigSelectors.selectConfigStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) =>
                status === LoadStatus.Error
                    ? throwError(() => new Error('ConfigService: init failed'))
                    : of(undefined as void),
            ),
        );
    }

    public getMaterialErrorCodeCountMax(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialErrorCodeCountMax: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialErrorCodeCountMax)();
    }

    public getCseEmailFax(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCseEmailFax: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCseEmailFax)();
    }

    /**
     * get the flag to show refresh XML button for forms
     *
     * @returns true if the refresh XML button should be shown
     * @throws {Error} if service is not initialized
     */
    public getShowRefreshXMLButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowRefreshXMLButton: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowRefreshXMLButton)();
    }

    public getDisplayCustomerContact(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerContact: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCustomerContact)();
    }

    public getShowLanguageSelectionDropdown(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowLanguageSelectionDropdown: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowLanguageSelectionDropdown)();
    }

    public getEscalationEmail(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEscalationEmail: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEscalationEmail)();
    }

    public getInstructionsUrl(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getInstructionsUrl: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectInstructionsUrl)();
    }

    /**
     * get the flag to show instructions documentation link for forms
     *
     * @returns true if the instructions documentation link should be shown
     * @throws {Error} if service is not initialized
     */
    public getShowDocumentationLink(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowDocumentationLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowDocumentationLink)();
    }

    public getUserFrontId(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getUserFrontId: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectUserFrontId)();
    }

    public getMaxInterimsCount(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getMaxInterimsCount: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectMaxInterimsCount)();
    }

    public getComplaintNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getComplaintNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectComplaintNotificationType(notificationType))();
    }

    /**
     * Get the CSE Currency string
     *
     * @returns the CSE Currency string
     * @throws {Error} if service is not initialized
     */
    public getExpenseCurrency(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getExpenseCurrency: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectExpenseCurrency)();
    }

    /**
     * Gets the CSE Country
     *
     * @returns the CSE Country
     * @throws {Error} if service is not initialized
     */
    public getCseCountry(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCseCountry: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCseCountry)();
    }

    public getShowInfoToCCC(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getShowInfoToCCC: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowInfoToCCC)();
    }

    /**
     * Gets the LegalCheckIEC62353Text to display for equipment checkbox
     *
     * @returns the LegalCheckIEC62353Text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckIEC62353Text: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckIEC62353Text)();
    }

    /**
     * Gets the LegalCheckIEC62353Code to compare with Equipment.Division
     *
     * @returns the LegalCheckIEC62353Code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckIEC62353Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckIEC62353Code: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckIEC62353Code)();
    }

    /**
     * Gets the LegalCheckPCResistanceText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckPCResistanceText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPCResistanceText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckPCResistanceText)();
    }

    /**
     * Gets the LegalCheckPCResistanceCode to compare with Equipment.Division
     *
     * @returns the LegalCheckPCResistanceCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPCResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPCResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckPCResistanceCode)();
    }

    /**
     * Gets the LegalCheckDeviceLeakageText to display additional control label for LegalCheckIEC62353Text
     *
     * @returns the LegalCheckDeviceLeakageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDeviceLeakageText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckDeviceLeakageText)();
    }

    /**
     * Gets the LegalCheckDeviceLeakageCode to compare with Equipment.Division
     *
     * @returns the LegalCheckDeviceLeakageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDeviceLeakageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDeviceLeakageCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckDeviceLeakageCode)();
    }

    /**
     * Gets the LegalCheckXRayText to display for equipment checkbox only for Country-Germany
     *
     * @returns the LegalCheckXRayText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckXRayText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckXRayText)();
    }

    /**
     * Gets the LegalCheckXRayCode to compare with Equipment.Division only for Country-Germany
     *
     * @returns the LegalCheckXRayCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckXRayCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckXRayCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckXRayCode)();
    }

    /**
     * Gets the LegalCheckRadioProtectionText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckRadioProtectionText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckRadioProtectionText)();
    }

    /**
     * Gets the LegalCheckRadioProtectionCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckRadioProtectionCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckRadioProtectionCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckRadioProtectionCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckRadioProtectionCode)();
    }

    /**
     * Gets the LegalCheckInstallationCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInstallationCheckText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckInstallationCheckText)();
    }

    /**
     * Gets the LegalCheckInstallationCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckInstallationCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInstallationCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInstallationCheckCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckInstallationCheckCode)();
    }

    /**
     * Gets the LegalCheckStatusCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckStatusCheckText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckStatusCheckText)();
    }

    /**
     * Gets the LegalCheckStatusCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the LegalCheckStatusCheckCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckStatusCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckStatusCheckCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckStatusCheckCode)();
    }

    /**
     * Gets the LegalCheckConsistencyCheckText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the LegalCheckConsistencyCheckText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyCheckText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckConsistencyCheckText)();
    }

    /**
     * Gets the LegalCheckConsistencyCheckCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check consistency check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyCheckCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckConsistencyCheckCode)();
    }

    /**
     * Gets the LegalCheckPartiallyCheckedText to display for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partially checked text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartiallyCheckedText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckPartiallyCheckedText)();
    }

    /**
     * Gets the LegalCheckPartiallyCheckedCode to compare with Equipment.Division only for Country-Switzerland
     *
     * @returns the legal check partially checked code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartiallyCheckedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartiallyCheckedCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckPartiallyCheckedCode)();
    }

    /**
     * Gets the getLegalCheckAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckAcceptanceTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckAcceptanceTestCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckAcceptanceTestCode)();
    }

    /**
     * Gets the getLegalCheckPartialAcceptanceTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check partial acceptance test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckPartialAcceptanceTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckPartialAcceptanceTestCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckPartialAcceptanceTestCode)();
    }

    /**
     * Gets the getLegalCheckHealthCheckCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check health check code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckHealthCheckCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHealthCheckCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckHealthCheckCode)();
    }

    /**
     * Gets the getLegalCheckConsistencyTestCode for equipment checkbox only for Country-Switzerland
     *
     * @returns the legal check consistency test code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckConsistencyTestCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckConsistencyTestCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckConsistencyTestCode)();
    }

    /**
     * Gets the LegalCheckNEN1010Text to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check NEN1010 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckNEN1010Text: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckNEN1010Text)();
    }

    /**
     * Gets the LegalCheckNEN1010Code to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check NEN1010 code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckNEN1010Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckNEN1010Code: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckNEN1010Code)();
    }

    /**
     * Gets the LegalCheckK3RoomText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check K3 room text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckK3RoomText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckK3RoomText)();
    }

    /**
     * Gets the LegalCheckK3RoomCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check K3 room code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckK3RoomCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckK3RoomCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckK3RoomCode)();
    }

    /**
     * Gets the LegalCheckMeasurementPerformedText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement performed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementPerformedText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckMeasurementPerformedText)();
    }

    /**
     * Gets the LegalCheckMeasurementPerformedCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement performed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementPerformedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementPerformedCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckMeasurementPerformedCode)();
    }

    /**
     * Gets the LegalCheckInsulationResistanceText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check insulation resistance text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInsulationResistanceText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckInsulationResistanceText)();
    }

    /**
     * Gets the LegalCheckInsulationResistanceCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check insulation resistance code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckInsulationResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckInsulationResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckInsulationResistanceCode)();
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededText to display for equipment checkbox only for Country-Netherlands
     *
     * @returns the legal check measurement not needed text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementNotNeededText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckMeasurementNotNeededText)();
    }

    /**
     * Gets the LegalCheckMeasurementNotNeededCode to compare with Equipment.Division only for Country-Netherlands
     *
     * @returns the legal check measurement not needed code
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckMeasurementNotNeededCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckMeasurementNotNeededCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckMeasurementNotNeededCode)();
    }

    /**
     * Break time between end-of-work and start-of-work.
     *
     * @returns the break time in hours
     * @throws {Error} if service is not initialized
     */
    public getEowSowBreakHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEowSowBreakHours: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEowSowBreakHours)();
    }

    /**
     * Time in hours that end-of-work can be moved into the future
     *
     * @returns the flexible hours for end-of-work
     * @throws {Error} if service is not initialized
     */
    public getEowFlexibleHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEowFlexibleHours: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEowFlexibleHours)();
    }

    /**
     * Determines the display of equipment based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowEquipment' has value 'CSE,APPS' , then return true
     *
     * @param taskGroup the task group of the notification
     * @returns true if the equipment should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showEquipment(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showEquipment: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowEquipment(taskGroup))();
    }

    /**
     * Determines the display of material based on the notification's taskGroup and the values from configSet
     * For eg. taskGroup = 'APPS' and configSet key = 'ShowMaterialConsumption' has value 'CSE' , then return false
     *
     * @param taskGroup the task group of the notification
     * @returns true if the material should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMaterialConsumption(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: showMaterialConsumption: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowMaterialConsumption(taskGroup))();
    }

    /**
     * Determines the display of MeasurementTools based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measurement tools should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasurementTools(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showMeasurementTools: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowMeasurementTools(taskGroup))();
    }

    /**
     * Determines the display of ParticipantList based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the participant list should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showParticipantList(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showParticipantList: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowParticipantList(taskGroup))();
    }

    /**
     * Determines the display of Follow-Up based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the follow-up should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showFollowup(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showFollowup: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFollowup(taskGroup))();
    }

    public showFollowupLongText(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showFollowupLongText: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFollowupLongText)();
    }

    public showFollowupAdditionalInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: showFollowupAdditionalInfo: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFollowupAdditionalInfo)();
    }

    /**
     * Determines the display of Measuring Points based on the notification's taskGroup and the values from configSet
     *
     * @param taskGroup the task group of the notification
     * @returns true if the measuring points should be shown, false otherwise
     * @throws {Error} if service is not initialized
     */
    public showMeasuringPoints(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: showMeasuringPoints: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowMeasuringPoints(taskGroup))();
    }

    public getFollowupDefaultAdditionalTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupDefaultAdditionalTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFollowupDefaultAdditionalTime)();
    }

    public getFollowupMinAdditionalTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupMinAdditionalTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFollowupMinAdditionalTime)();
    }

    public getFollowupAdditionalTimeStep(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowupAdditionalTimeStep: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFollowupAdditionalTimeStep)();
    }

    public getAppsTaskCode(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppsTaskCode: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAppsTaskCode)();
    }

    public getStatusAfterFixValueForQCFlag(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getStatusAfterFixValueForQCFlag: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectStatusAfterFixValueForQCFlag)();
    }

    /**
     * get the ETA limit from config service
     *
     * @returns the ETA limit in hours
     * @throws {Error} if service is not initialized
     */
    public getEtaLimitMinutes(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEtaLimitMinutes: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEtaLimitMinutes)();
    }

    public getMaterialHistoryLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialHistoryLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialHistoryLimit)();
    }

    /**
     * Configuration to determine how many days back in time
     * is the limit to retrieve notifications
     *
     * @example 30 retrieve notifications for the last 30 days
     * Used for DocBox side menu to display notifications history list
     *
     * @returns number representing the limit of days i.e.: 30, 60, 90
     * @throws {Error} if service is not initialized
     */
    public getNotificationsHistoryDaysLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getNotificationsHistoryDaysLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectNotificationsHistoryDaysLimit)();
    }

    /**
     * get the headquarter detail line 1 from config service
     *
     * @returns the headquarter detail line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ1(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ1: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ1)();
    }

    /**
     * get the headquarter detail line 2 from config service
     *
     * @returns the headquarter detail line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ2(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ2: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ2)();
    }

    /**
     * get the headquarter detail line 3 from config service
     *
     * @returns the headquarter detail line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ3(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ3: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ3)();
    }

    /**
     * get the headquarter detail line 4 from config service
     *
     * @returns the headquarter detail line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ4(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ4: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ4)();
    }

    /**
     * get the headquarter detail link label from config service
     *
     * @returns the headquarter detail link label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ5: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ5)();
    }

    /**
     * get the headquarter detail link from config service
     *
     * @returns the headquarter detail link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQ5Link(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQ5Link: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQ5Link)();
    }

    /**
     * get the headquarter detail telephone number details from config service
     *
     * @returns the headquarter detail telephone number details
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoHQTel(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCorpInfoHQTel: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoHQTel)();
    }

    /**
     * get the Support hours for corporate Information for NGMAA
     *
     * @returns the Support hours for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportHours(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportHours: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoSupportHours)();
    }

    /**
     * get the Support Email for corporate Information for NGMAA
     *
     * @returns the Support Email for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportEmail(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportEmail: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoSupportEmail)();
    }

    /**
     * get the Support link for corporate Information for NGMAA
     *
     * @returns the Support link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoSupportLink)();
    }

    /**
     * get the Support create ticket link for corporate Information for NGMAA
     *
     * @returns the Support create ticket link for corporate Information for NGMAA
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoSupportCreateTicketLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoSupportCreateTicketLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoSupportCreateTicketLink)();
    }

    /**
     * get the legal info line 1 from config service
     *
     * @returns the legal info line 1
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo1(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo1: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo1)();
    }

    /**
     * get the legal info line 2 from config service
     *
     * @returns the legal info line 2
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo2(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo2: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo2)();
    }

    /**
     * get the legal info line 3 from config service
     *
     * @returns the legal info line 3
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo3(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo3: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo3)();
    }

    /**
     * get the legal info line 4 from config service
     *
     * @returns the legal info line 4
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo4(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo4: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo4)();
    }

    /**
     * get the legal info line 5 from config service
     *
     * @returns the legal info line 5
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo5(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo5: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo5)();
    }

    /**
     * get the legal info line 6 from config service
     *
     * @returns the legal info line 6
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo6(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo6: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo6)();
    }

    /**
     * get the legal info line 7 from config service
     *
     * @returns the legal info line 7
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfo7(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfo7: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfo7)();
    }

    /**
     * get the legal info policy Label from config service
     *
     * @returns the legal info policy Label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicy(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoPolicy: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfoPolicy)();
    }

    /**
     * get the legal info policy link from config service
     *
     * @returns the legal info policy link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoPolicyLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoPolicyLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfoPolicyLink)();
    }

    /**
     * get the legal info TOU label from config service
     *
     * @returns the legal info TOU label
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOU(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoTOU: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfoTOU)();
    }

    /**
     * get the legal info TOU link from config service
     *
     * @returns the legal info TOU link
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalInfoTOULink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalInfoTOULink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalInfoTOULink)();
    }

    /**
     * get the legal info policy month from config service
     *
     * @returns the legal info policy month
     * @throws {Error} if service is not initialized
     */
    public getCorpInfoLegalPolicyMonth(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCorpInfoLegalPolicyMonth: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCorpInfoLegalPolicyMonth)();
    }

    /**
     * get the global storage location from config service
     *
     * @returns the global storage location
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocation(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocation: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectGlobalStorageLocation)();
    }

    /**
     * get the global storage location for Material Pricing screen
     *
     * @returns the global storage location for Material Pricing screen
     * @throws {Error} if service is not initialized
     */
    public getGlobalStorageLocationMatPricing(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocationMatPricing: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectGlobalStorageLocationMatPricing)();
    }

    public getGlobalStorageLocationMatRequest(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getGlobalStorageLocationMatRequest: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectGlobalStorageLocationMatRequest)();
    }

    /**
     * get the Commercial Info default text from config service
     *
     * @returns the Commercial Info default text
     * @throws {Error} if service is not initialized
     */
    public getCIDefaultText(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCIDefaultText: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCIDefaultText)();
    }

    /**
     * get the Previous days limit the timesheet can be filled up
     *
     * @returns the Previous days limit the timesheet can be filled up
     * @throws {Error} if service is not initialized
     */
    public getPreviousDaysTimesheetLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPreviousDaysTimesheetLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectPreviousDaysTimesheetLimit)();
    }

    /**
     * get the maximum allowed future time(in minutes) the timesheet can be submitted
     *
     * @returns the maximum allowed future time(in minutes) the timesheet can be submitted
     * @throws {Error} if service is not initialized
     */
    public getMaxAllowedFutureTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxAllowedFutureTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaxAllowedFutureTime)();
    }

    /**
     * get the default followup time for NGMP58
     *
     * @returns the default followup time for NGMP58
     * @throws {Error} if service is not initialized
     */
    public getDefaultFollowUpTimeP58(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultFollowUpTimeP58: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDefaultFollowUpTimeP58)();
    }

    /**
     * get the flag to switch on using the endpoint for customised forms
     *
     * @returns the flag to switch on using the endpoint for customised forms
     * @throws {Error} if service is not initialized
     */
    public getUseCustomisedForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomisedForms: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseCustomisedForms)();
    }

    /**
     * Get the Legal requirement checks for Germany in-vitro
     *             --Text--                       --Code--
     * Checkbox Checked according VDE0701/0702      VDE1
     *       Protective Conductor Resistant in mOhm VDE2
     *       Equivalent Device leakage in mA        VDE3
     *       Insulation Resistant in MOhm           VDE4
     *
     * @returns the legal check VDE0701/0702 text
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckVDE701702Text(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDE701702Text: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDE701702Text)();
    }

    public getLegalCheckVDE701702Code(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDE701702Code: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDE701702Code)();
    }

    public getLegalCheckVDEPCResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEPCResistanceText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEPCResistanceText)();
    }

    public getLegalCheckVDEPCResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEPCResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEPCResistanceCode)();
    }

    public getLegalCheckVDEDeviceLeakageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEDeviceLeakageText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEDeviceLeakageText)();
    }

    public getLegalCheckVDEDeviceLeakageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEDeviceLeakageCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEDeviceLeakageCode)();
    }

    public getLegalCheckVDEInsulationResistanceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEInsulationResistanceText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEInsulationResistanceText)();
    }

    public getLegalCheckVDEInsulationResistanceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckVDEInsulationResistanceCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckVDEInsulationResistanceCode)();
    }

    // get the flag to switch on remove 9999 from error code
    public getUseNoCustomErrorCode(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseNoCustomErrorCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseNoCustomErrorCode)();
    }

    // get the flag to switch on Add Material Return
    public getUseAddNewMaterialReturn(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseAddNewMaterialReturn: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseAddNewMaterialReturn)();
    }

    // get the report text length as defined for Sales org / Plant
    public getReportTextLineLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportTextLineLength: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectReportTextLineLength)();
    }

    // get the report text max num of allowed lines as defined for Sales org / Plant
    public getReportTextMaxLines(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportTextMaxLines: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectReportTextMaxLines)();
    }

    public getMaxRecipientEntries(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxRecipientEntries: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaxRecipientEntries)();
    }

    public getSelectDefaultFollowupReason(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSelectDefaultFollowupReason: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSelectDefaultFollowupReason)();
    }

    public getEnableSafetyAssessmentForm(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableSafetyAssessmentForm: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableSafetyAssessmentForm)();
    }

    public getBootstockReplenishEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getBootstockReplenishEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectBootstockReplenishEnabled)();
    }

    public getSafetyAssessmentForm(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSafetyAssessmentForm: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSafetyAssessmentForm)();
    }

    public getEnablePaceFormsCrossDeviceSync(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnablePaceFormsCrossDeviceSync: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnablePaceFormsCrossDeviceSync)();
    }

    public getDisplayAdditionalMaterialRequestInformation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalMaterialRequestInformation: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAdditionalMaterialRequestInformation)();
    }

    public getAddMaterialCurrencyCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialCurrencyCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAddMaterialCurrencyCode)();
    }

    public getAddMaterialSelectBillableDefault(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialSelectBillableDefault: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAddMaterialSelectBillableDefault)();
    }

    public getAddMaterialBillingIndicator(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialBillingIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAddMaterialBillingIndicator)();
    }

    public getAddMaterialBillingChangeable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddMaterialBillingChangeable: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAddMaterialBillingChangeable)();
    }

    public getShowCommercialInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCommercialInfo: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowCommercialInfo)();
    }

    /**
     * Gets the CSML Tracker Link to track the material Ordered
     *
     * @returns the CSML Tracker Link
     * @throws {Error} if service is not initialized
     */
    public getCSMLTrackerLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCSMLTrackerLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCSMLTrackerLink)();
    }

    public getAddDeliveryAddressTaxi(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAddDeliveryAddressTaxi: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAddDeliveryAddressTaxi)();
    }

    public getDisablePaceForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisablePaceForms: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectDisablePaceForms)();
    }

    public getDisplayNonProductiveHours(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayNonProductiveHours: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayNonProductiveHours)();
    }

    public getDisplayAdditionalTravelBack(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelBack: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAdditionalTravelBack)();
    }

    public getDisplayTravelBackSummary(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTravelBackSummary: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayTravelBackSummary)();
    }

    public getDisplayAdditionalTravelInfo(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelInfo: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAdditionalTravelInfo)();
    }

    public getDisplayMeansOfTransport(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMeansOfTransport: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayMeansOfTransport)();
    }

    public getDisplayAdditionalMaterialReturnInformation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalMaterialReturnInformation: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAdditionalMaterialReturnInformation)();
    }

    public getFormatMultilineTextPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFormatMultilineTextPattern: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFormatMultilineTextPattern)();
    }

    public getFormatInternalInfoMultilineTextPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFormatInternalInfoMultilineTextPattern: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFormatInternalInfoMultilineTextPattern)();
    }

    public getDivisionsWithAdditionalLegalText(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithAdditionalLegalText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDivisionsWithAdditionalLegalText(division))();
    }

    public getDivisionsWithIEC62353(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithIEC62353: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDivisionsWithIEC62353(division))();
    }

    public getDivisionsWithVDE701702(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithVDE701702: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDivisionsWithVDE701702(division))();
    }

    /**
     * Get the form types for which the participant copy workflow should be enabled for a given notification type.
     * Returns a string array instead of FormsConstants.FORMTYPES
     * a) to avoid a cyclic dependency
     * b) since the values from GW are given as strings anyway, so it does not matter where we cast them
     *
     * @param notificationType The notification type for which to get the supported form types
     * @returns string[] A list of form types that should support the participant copy workflow for this notification type
     * @throws {Error} if service is not initialized
     */
    public getParticipantCopySupportByNotificationType(notificationType: ConfigConstants.NotificationTypes): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getParticipantCopySupportByNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(
            ConfigSelectors.selectParticipantCopySupportByNotificationType(notificationType),
        )();
    }

    /**
     * Get the config to determine if moment need to be created in UTC or no
     *
     * @returns true if moment need to be created in UTC
     * @throws {Error} if service is not initialized
     */
    public getFormatMomentInUTC(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getFormatMomentInUTC: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectFormatMomentInUTC)();
    }

    public getDisplayTimesheetAddress(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTimesheetAddress: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayTimesheetAddress)();
    }

    public getTravelBackStartEditable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTravelBackStartEditable: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTravelBackStartEditable)();
    }

    public getTravelBackEndEditable(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTravelBackEndEditable: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTravelBackEndEditable)();
    }

    public getTimesheetEndTimeLimit(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetEndTimeLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTimesheetEndTimeLimit)();
    }

    public getDisplayTimesheetAddressOptions(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTimesheetAddressOptions: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayTimesheetAddressOptions)();
    }

    public getTimesheetAddressHome(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetAddressHome: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTimesheetAddressHome)();
    }

    public getTimesheetAddressOffice(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetAddressOffice: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTimesheetAddressOffice)();
    }

    public getMatPrioritiesForDOP(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatPrioritiesForDOP: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatPrioritiesForDOP)();
    }

    public getMatPrioritiesForCustomer(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatPrioritiesForCustomer: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatPrioritiesForCustomer)();
    }

    public getDisplayMaterialQuote(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMaterialQuote: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayMaterialQuote)();
    }

    public getDisplayCustomerSignatureInForm(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerSignatureInForm: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCustomerSignatureInForm)();
    }

    public getTimesheetPauseCodes(taskType: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getTimesheetPauseCodes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectTimesheetPauseCodes(taskType))();
    }

    public getSparePartsCatalogLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSparePartsCatalogLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSparePartsCatalogLink)();
    }

    public getDisableQuoteForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisableQuoteForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisableQuoteForNotificationTypes(notificationType))();
    }

    public getEscalateNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEscalateNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEscalateNotificationTypes(notificationType))();
    }

    public getDefaultReasonCodeForCentralScrap(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultReasonCodeForCentralScrap: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDefaultReasonCodeForCentralScrap)();
    }

    public getDisableSpecialTransportAWBfield(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisableSpecialTransportAWBfield: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisableSpecialTransportAWBfield)();
    }

    public getSpecialDangerousGoodsInd(dangerousGoodInd: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSpecialDangerousGoodsInd: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSpecialDangerousGoodsInd(dangerousGoodInd))();
    }

    public getDangerousGoodsIndicator(dangerousGoodInd: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDangerousGoodsIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDangerousGoodsIndicator(dangerousGoodInd))();
    }

    public getFollowUpDefaultDays(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFollowUpDefaultDays: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFollowUpDefaultDays)();
    }

    /**
     * Determines whether to show Planned ETA alert for the notifications based on the value from the config set
     *
     * @returns true if the alert should be shown
     * @throws {Error} if service is not initialized
     */
    public getDisplayEtaAlert(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisplayEtaAlert: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayEtaAlert)();
    }

    /**
     * get the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     *
     * @returns the timer limit(in minutes) for the Alert message to be displayed before the Planned ETA
     * @throws {Error} if service is not initialized
     */
    public getEtaAlertTimerLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEtaAlertTimerLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEtaAlertTimerLimit)();
    }

    public getTextClausePattern(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getTextClausePattern: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectTextClausePattern)();
    }

    public getShowMobileNumberForParticipants(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowMobileNumberForParticipants: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowMobileNumberForParticipants)();
    }

    public getShowCalibrationDate(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCalibrationDate: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowCalibrationDate)();
    }

    public getUseCustomTimeInput(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomTimeInput: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseCustomTimeInput)();
    }

    public getShowCustomerInformationOnSummary(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCustomerInformationOnSummary: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowCustomerInformationOnSummary)();
    }

    public getFormatMultilineTextOnBlur(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getFormatMultilineTextOnBlur: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectFormatMultilineTextOnBlur)();
    }

    public getShowFormCustomerEmailData(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFormCustomerEmailData: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFormCustomerEmailData)();
    }

    public getUseCustomDurationStepper(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCustomDurationStepper: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseCustomDurationStepper)();
    }

    public getPrefillTroubleText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPrefillTroubleText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectPrefillTroubleText)();
    }

    public getEnableJobOnFly(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableJobOnFly: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableJobOnFly)();
    }

    public getJobOnFlyDefaultDuration(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getJobOnFlyDefaultDuration: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectJobOnFlyDefaultDuration)();
    }

    public getDisplayMaterialPricing(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayMaterialPricing: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayMaterialPricing)();
    }

    public getActivateCosima(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getActivateCosima: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectActivateCosima)();
    }

    public getDisplayBatchNumber(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayBatchNumber: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayBatchNumber)();
    }

    public getDisplayWeeklyWorkTime(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayWeeklyWorkTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayWeeklyWorkTime)();
    }

    public getEnableStandbyTravel(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableStandbyTravel: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableStandbyTravel)();
    }

    public getIsTravelBillable(orderType: string): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getIsTravelBillable: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectIsTravelBillable(orderType))();
    }

    public getDisplayCentralScrap(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCentralScrap: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCentralScrap)();
    }

    public getDisplayCentralScrapForDangerousGoods(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCentralScrapForDangerousGoods: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCentralScrapForDangerousGoods)();
    }

    public getSmartSensorTracker(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSmartSensorTracker: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSmartSensorTracker)();
    }

    public getSmartSensorTrackerUrl(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSmartSensorTrackerUrl: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSmartSensorTrackerUrl)();
    }

    public getMatSerTypeaheadToggle(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatSerTypeaheadToggle: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatSerTypeaheadToggle)();
    }

    public getMatLTextDisplayLines(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatLTextDisplayLines: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatLTextDisplayLines)();
    }

    public getDisplayAdditionalTravelCost(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAdditionalTravelCost: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAdditionalTravelCost)();
    }

    public getAdditionalTravelCostText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAdditionalTravelCostText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAdditionalTravelCostText)();
    }

    public getMaxWeeklyWorkingHours(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxWeeklyWorkingHours: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaxWeeklyWorkingHours)();
    }

    public getDisplayCseSignature(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCseSignature: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCseSignature)();
    }

    public getDisplayBootStockIndicator(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayBootStockIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayBootStockIndicator)();
    }

    public getEnableBootstockScrap(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBootstockScrap: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableBootstockScrap)();
    }

    /**
     * Pausing notifications is no longer supported, but we need to return true to ensure that notifications can be
     * started in parallel.
     */
    public getAllowNotificationPause(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAllowNotificationPause: Trying to call ConfigService before initialization!`,
            );
        }
        return true;
    }

    public getDisplayTrendCodeInFollowup(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayTrendCodeInFollowup: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayTrendCodeInFollowup)();
    }

    public getShowMeasurementToolsDetails(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowMeasurementToolsDetails: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowMeasurementToolsDetails)();
    }

    public getHideParticipantFormSubmission(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideParticipantFormSubmission: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideParticipantFormSubmission)();
    }

    public getShowDataMediumInEquipment(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowDataMediumInEquipment: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowDataMediumInEquipment)();
    }

    public getAnaLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAnaLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAnaLink)();
    }

    public getShowIconForMaterialDelivery(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowIconForMaterialDelivery: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowIconForMaterialDelivery)();
    }

    public getTeamplayFleetLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getTeamplayFleetLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectTeamplayFleetLink)();
    }

    public getComplaintsQuestionnaireLink(appID = ''): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getcomplaintsQuestionnaireLink${appID}: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectComplaintsQuestionnaireLink(appID))();
    }

    public getSparePartCatalogMatLink(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSparePartCatalogMatLink: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSparePartCatalogMatLink)();
    }

    public getEnableTimesheetEmail(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTimesheetEmail: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableTimesheetEmail)();
    }

    public isMaterialLiBatteryStandAlone(unNumber = ''): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialLiBatteryStandAlone: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialLiBatteryStandAlone(unNumber))();
    }

    public isMaterialLiBatteryInDevice(unNumber = ''): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialLiBatteryInDevice: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialLiBatteryInDevice(unNumber))();
    }

    public isTimesheetDisabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: isTimesheetDisabled: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectIsTimesheetDisabled)();
    }

    public getDataManagementEmail(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDataManagementEmail: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDataManagementEmail)();
    }

    public getDataManagementEmailInvitro(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDataManagementEmailInvitro: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDataManagementEmailInvitro)();
    }

    public getShowFleetLinkInEquipmentHistory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFleetLinkInEquipmentHistory: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFleetLinkInEquipmentHistory)();
    }

    public getShowStartImmediately(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowStartImmediately: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowStartImmediately)();
    }

    public getShowPlaceHolderForReportText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowPlaceHolderForReportText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowPlaceHolderForReportText)();
    }

    public getShowPlaceHolderForEscalationText(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowPlaceHolderForEscalationText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowPlaceHolderForEscalationText)();
    }

    public getMaxWorkingHoursInTimesheet(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaxWorkingHoursInTimesheet: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaxWorkingHoursInTimesheet)();
    }

    /**
     * @deprecated The list of userStatus types does not change and does not need to be read from gateway; therefore no
     * longer using this function.
     *
     * Model value from configSet.json is comma separated string i.e. "PSI,PPI"
     * User status type in parameter is also comma separated string i.e. "PSI,REMT"
     * After splitting  ["PSI","REMT"]
     * Iterating above ("PSI,PPI").indexOf(PSI) is 0 returns true
     *
     * @param userStatusType - user status type
     * @returns true if userStatusType is in the model value
     */
    public getHideComplaintsForUserStatusType(userStatusType: ConfigConstants.USER_STATUS): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideComplaintsForUserStatusType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideComplaintsForUserStatusType(userStatusType))();
    }

    public getBackendName(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getBackendName: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectBackendName)();
    }

    public isMaterialOrderPriorityForSplit(materialOrderPriority: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isMaterialOrderPriorityForSplit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderPriorityForSplit(materialOrderPriority))();
    }

    public getDocboxDoctypes(): Array<{ key: string; value: string }> {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDocboxDoctypes: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectDocboxDoctypes)();
    }

    public getDocBoxInstallationDocType(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocBoxInstallationDocType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDocBoxInstallationDocType)();
    }

    public getDocBoxEnableReportTypeSelection(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocBoxEnableReportTypeSelection: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDocBoxEnableReportTypeSelection)();
    }

    public getUserCountryFI(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getUserCountryFI: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectUserCountryFI)();
    }

    public getDisclaimerForChinaFI(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisclaimerForChinaFI: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisclaimerForChinaFI)();
    }

    /**
     * SAP gateway does not support the full unicode character set. To avoid recurring problems with users entering
     * unsupported characters, we know check all text input for unsupported characters and mark them as error.
     * Since input fields can be used before the config service init was completed, we also return a default value in
     * those cases, e.g. to let users still report errors during startup of the application.
     *
     * Ideally we would also fix the naming here and on gateway to something like "getUnsupportedUnicodeRegex", but
     * what has been done has been done. Take this comment as explanation instead.
     *
     * @returns {string} - regex string to match unsupported unicode characters
     */
    public getEmojisRegex(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEmojisRegex: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEmojisRegex)();
    }

    public getLegal3551NotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551NotificationTypes(notificationType))();
    }

    public getLegal3551DetachPowerCordText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551DetachPowerCordText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551DetachPowerCordText)();
    }

    public getLegal3551PermanentConnectedText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551PermanentConnectedText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551PermanentConnectedText)();
    }

    public getLegal3551NonDetachPowerCordText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NonDetachPowerCordText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551NonDetachPowerCordText)();
    }

    public getLegal3551DetachPowerCordCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551DetachPowerCordCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551DetachPowerCordCode)();
    }

    public getLegal3551PermanentConnectedCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551PermanentConnectedCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551PermanentConnectedCode)();
    }

    public getLegal3551NonDetachPowerCordCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegal3551NonDetachPowerCordCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegal3551NonDetachPowerCordCode)();
    }

    public getLegalVerificationNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerificationNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalVerificationNotificationTypes(notificationType))();
    }

    public getDivisionsWithVerificationLegalCheck(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDivisionsWithVerificationLegalCheck: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDivisionsWithVerificationLegalCheck(division))();
    }

    public getLegalVerifiedSecurityDeviceText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerifiedSecurityDeviceText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalVerifiedSecurityDeviceText)();
    }

    public getLegalVerifiedSecurityDeviceCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalVerifiedSecurityDeviceCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalVerifiedSecurityDeviceCode)();
    }

    public getLegalResumptionAfterUserValidationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalResumptionAfterUserValidationText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalResumptionAfterUserValidationText)();
    }

    public getLegalResumptionAfterUserValidationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalResumptionAfterUserValidationCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalResumptionAfterUserValidationCode)();
    }

    public getLegalCheckHandoverSafetyInformationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHandoverSafetyInformationText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckHandoverSafetyInformationText)();
    }

    public getLegalCheckHandoverUserDocumentationText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHandoverUserDocumentationText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckHandoverUserDocumentationText)();
    }

    public getLegalCheckHandoverSafetyInformationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHandoverSafetyInformationCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckHandoverSafetyInformationCode)();
    }

    public getLegalCheckHandoverUserDocumentationCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckHandoverUserDocumentationCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckHandoverUserDocumentationCode)();
    }

    public getDisplayConfirmSignatures(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayConfirmSignatures: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayConfirmSignatures)();
    }

    public getEnableRemoteWork(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableRemoteWork: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableRemoteWork)();
    }

    public getShowRemoteStatusChangeWarning(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowRemoteStatusChangeWarning: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowRemoteStatusChangeWarning)();
    }

    public getDisplayDocBox(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getDisplayDocBox: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayDocBox)();
    }

    public getShowUploadToDocBoxForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowUploadToDocBoxForNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowUploadToDocBoxForNotificationType(notificationType))();
    }

    public getUploadToDocBoxMandatoryForNotificationType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxMandatoryForNotificationType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(
            ConfigSelectors.selectUploadToDocBoxMandatoryForNotificationType(notificationType),
        )();
    }

    public getUploadToDocBoxStatusMandatory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxStatusMandatory: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUploadToDocBoxStatusMandatory)();
    }

    public getUploadToDocBoxAllowedMimeTypes(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUploadToDocBoxAllowedMimeTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUploadToDocBoxAllowedMimeTypes)();
    }

    public getMatQuoteDistanceUnit(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatQuoteDistanceUnit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatQuoteDistanceUnit)();
    }

    public getEnableMatQuoteDistance(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMatQuoteDistance: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableMatQuoteDistance)();
    }

    public getEnableHideTimesheetAddress(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableHideTimesheetAddress: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableHideTimesheetAddress)();
    }

    public getHideTimesheetPersonnelSubArea(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideTimesheetPersonnelSubArea: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideTimesheetPersonnelSubArea)();
    }

    public getShowTpfReportVisibility(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowTpfReportVisibility)();
    }

    public getMandatoryTpfReportVisibility(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMandatoryTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMandatoryTpfReportVisibility)();
    }

    public getDefaultTpfReportVisibility(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDefaultTpfReportVisibility: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDefaultTpfReportVisibility)();
    }

    public getEquipmentErrorCodeRemoteWork(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEquipmentErrorCodeRemoteWork: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEquipmentErrorCodeRemoteWork)();
    }

    public getDisplayConfirmationDistance(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayConfirmationDistance: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayConfirmationDistance)();
    }

    public getDisplayConfirmationExpenses(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayConfirmationExpenses: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayConfirmationExpenses)();
    }

    public getDowntimeThresholdInMinutes(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDowntimeThresholdInMinutes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDowntimeThresholdInMinutes)();
    }

    public getEnableIndividualDocBoxDownload(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableIndividualDocBoxDownload: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableIndividualDocBoxDownload)();
    }

    public getPlaceHolderForCustomerIdentification(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getPlaceHolderForCustomerIdentification: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectPlaceHolderForCustomerIdentification)();
    }

    public getShowDataMaintenanceEnhancement(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowDataMaintenanceEnhancement: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowDataMaintenanceEnhancement)();
    }

    public getDisplayServicePartQuestionnaire(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayServicePartQuestionnaire: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayServicePartQuestionnaire)();
    }

    public getMaterialOrderTextInfoLineLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoLineLength: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderTextInfoLineLength)();
    }

    public getMaterialOrderTextInfoMaxLines(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoMaxLines: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderTextInfoMaxLines)();
    }

    public getMaterialOrderTextInfoPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderTextInfoPattern: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderTextInfoPattern)();
    }

    public getCSMLInvivoPhoneNumber(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCSMLInvivoPhoneNumber: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCSMLInvivoPhoneNumber)();
    }

    public getCSMLInvitroPhoneNumber(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCSMLInvitroPhoneNumber: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCSMLInvitroPhoneNumber)();
    }

    public getEnableTextInfoValidation(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTextInfoValidation: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableTextInfoValidation)();
    }

    public getByBoxKonnectLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getByBoxKonnectLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectByBoxKonnectLink)();
    }

    public getCBDocLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getCBDocLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectCBDocLink)();
    }

    public getSeSoLink(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getSeSoLink: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectSeSoLink)();
    }

    public getSeSoDivisions(): string[] {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getSeSoDivisions: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectSeSoDivisions)();
    }

    public getEnableMatReturnWorkAround(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMatReturnWorkAround: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableMatReturnWorkAround)();
    }

    public getDisplayAddMaterialButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayAddMaterialButton: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayAddMaterialButton)();
    }

    public getEnableBillingIndicator(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBillingIndicator: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableBillingIndicator)();
    }

    public getDisplayCustomerPrice(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplayCustomerPrice: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplayCustomerPrice)();
    }

    public getEnableManualIndividualization(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableManualIndividualization: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableManualIndividualization)();
    }

    public getEnableDocumentFeedbackButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDocumentFeedbackButton: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableDocumentFeedbackButton)();
    }

    public getEnableFormsHistoricalValues(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableFormsHistoricalValues: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableFormsHistoricalValues)();
    }

    public getMeasurementToolsDefaultSettingsAPPS(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMeasurementToolsDefaultSettingsAPPS: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMeasurementToolsDefaultSettingsAPPS)();
    }

    public getHideRecoveryTimeForNotificationTypes(
        notificationType: ConfigConstants.NotificationTypes,
        notificationCategory: string[],
    ): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideRecoveryTimeForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(
            ConfigSelectors.selectHideRecoveryTimeForNotificationTypes(notificationType, notificationCategory),
        )();
    }

    public getMaterialOrderDeliveryOptions(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialOrderDeliveryOptions: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderDeliveryOptions)();
    }

    public getMaterialReturnAddressOptions(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMaterialReturnAddressOptions: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialReturnAddressOptions)();
    }

    public getShowFCCForNotificationTypes(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowFCCForNotificationTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowFCCForNotificationTypes(notificationType))();
    }

    public getBKInfoTextForBillingIndicatorsNotReceived(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getBKInfoTextForBillingIndicatorsNotReceived: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectBKInfoTextForBillingIndicatorsNotReceived)();
    }

    public getShowCyberSecurityQuestion(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowCyberSecurityQuestion: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowCyberSecurityQuestion)();
    }

    public getHideAbortAllFormsButton(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideAbortAllFormsButton: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideAbortAllFormsButton)();
    }

    public getEnableAbortFormInList(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableAbortFormInList: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableAbortFormInList)();
    }

    /**
     * Get the max char limit for Sub address fields i.e. Department, Floor, Room for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for sub address fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestSubAddressCharLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatRequestSubAddressCharLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatRequestSubAddressCharLimit)();
    }

    /**
     * Get the max char limit for contact fields i.e. Phone, Contact for Customer Selection in Material Request form
     * If value is not coming from GW Config Set then by default it would return value from constant in FE
     *
     * @returns {number} - max char limit for contact fields
     * @throws {Error} if service is not initialized
     */
    public getMatRequestContactCharLimit(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getMatRequestContactCharLimit: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMatRequestContactCharLimit)();
    }

    public getCommercialInfMandatoryForNonBill(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCommercialInfMandatoryForNonBill: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCommercialInfMandatoryForNonBill)();
    }

    public getEnableTextInfoForDop(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableTextInfoForDop: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableTextInfoForDop)();
    }

    public getHideSubmitButtonForForms(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideSubmitButtonForForms: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideSubmitButtonForForms)();
    }

    public getHideAuthorsFormsButtons(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getHideAuthorsFormsButtons: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectHideAuthorsFormsButtons)();
    }

    public getShowBootOtherStockWarning(taskGroup: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowBootOtherStockWarning: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowBootOtherStockWarning(taskGroup))();
    }

    /**
     * Gets the LegalCheckDosageCode to display for equipment checkbox only for Country-Spain
     *
     * @returns the LegalCheckDosageCode
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageCode(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDosageCode: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckDosageCode)();
    }

    /**
     * Gets the LegalCheckDosageText to compare with Equipment.Division only for Country-Spain
     *
     * @returns the LegalCheckDosageText
     * @throws {Error} if service is not initialized
     */
    public getLegalCheckDosageText(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getLegalCheckDosageText: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectLegalCheckDosageText)();
    }

    public getEnableStatesOfUSADropdown(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableStatesOfUSADropdown: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableStatesOfUSADropdown)();
    }

    /**
     * Gets the DeliveryOptionsEnabled to enable priority shipping logic
     *
     * @returns the DeliveryOptionsEnabled
     * @throws {Error} if service is not initialized
     */
    public getDeliveryOptionsEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDeliveryOptionsEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDeliveryOptionsEnabled)();
    }

    /**
     * Gets the DeliveryApprovalMinimumLength to get minimum length for customer approver in material order screen
     *
     * @returns the DeliveryApprovalMinimumLength
     * @throws {Error} if service is not initialized
     */
    public getDeliveryApprovalMinimumLength(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDeliveryApprovalMinimumLength: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDeliveryApprovalMinimumLength)();
    }

    public isCriticalityApplicableOnNotifType(notificationType: ConfigConstants.NotificationTypes): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isCriticalityApplicableOnNotifType: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCriticalityApplicableOnNotifType(notificationType))();
    }

    public isCustomerDamageEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isCustomerDamageEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectIsCustomerDamageEnabled)();
    }

    public isFocReplacementEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isFocReplacementEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectIsFocReplacementEnabled)();
    }

    public getEnabledAfterHoursCallOut(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnabledAfterHoursCallOut: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnabledAfterHoursCallOut)();
    }

    public getEnableOtherTasks(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableOtherTasks: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableOtherTasks)();
    }

    public getOtherTasksOverlapUpcomingNoti(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getOtherTasksOverlapUpcomingNoti: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectOtherTasksOverlapUpcomingNoti)();
    }

    /**
     * Gets the DisplaySideMenuDocBoxFlag to enable DocBox
     * entry in the side menu
     *
     * @returns the DisplaySideMenuDocBoxFlag
     * @throws {Error} if service is not initialized
     */
    public getDisplaySideMenuDocBoxFlag(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDisplaySideMenuDocBoxFlag: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDisplaySideMenuDocBoxFlag)();
    }

    public getReportAnIssueAllowedMimeTypes(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getReportAnIssueAllowedMimeTypes: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectReportAnIssueAllowedMimeTypes)();
    }

    /**
     * Countries will have the possibility to select participant's attendee (credit) type
     * between Non-credit or Credit or N/A when adding a new attendee => radio buttons
     * Current business rule is only PaceUS will have this selector
     * enabled
     *
     * @returns the boolean representing the ParticipantCreditTypeSelectorEnabled enabled
     * @throws {Error} if config service is not initialized
     */
    public getParticipantCreditTypeSelectorEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getParticipantCreditTypeSelectorEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectParticipantCreditTypeSelectorEnabled)();
    }

    public getEnableDurationChange(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDurationChange: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableDurationChange)();
    }

    /**
     * Gets the value for Delivery Cut off time enabled
     *
     * @returns the getEnableDeliveryCutoffTime boolean
     * @throws {Error} if service is not initialized
     */
    public getEnableDeliveryCutoffTime(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableDeliveryCutoffTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableDeliveryCutoffTime)();
    }

    public getDocMissingProtocolsEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDocMissingProtocolsEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDocMissingProtocolsEnabled)();
    }

    public getEnableFDA2579Form(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableFDA2579Form: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableFDA2579Form)();
    }

    public isSourceReplacementEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isSourceReplacementEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectIsSourceReplacementEnabled)();
    }

    public getCentricarePCHEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCentricarePCHEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCentricarePCHEnabled)();
    }

    public getCentricarePCHWarningTime(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getCentricarePCHWarningTime: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectCentricarePCHWarningTime)();
    }

    public getRadioactiveSurveyEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyEnabled: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectRadioactiveSurveyEnabled)();
    }

    public getRadioactiveSurveyDivisions(division: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyDivisions: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectRadioactiveSurveyDivisions(division))();
    }

    public getRadioactiveSurveyToolCalibration(): number | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyToolCalibration: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectRadioactiveSurveyToolCalibration)();
    }

    public getRadioactiveSurveyRegion(region: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRadioactiveSurveyRegion: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectRadioactiveSurveyRegions(region))();
    }

    public getPickupRFIDEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getPickupRFIDEnabled: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectPickupRFIDEnabled)();
    }

    public getEnableAWBPackaging(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableAWBPackaging: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableAWBPackaging)();
    }

    public getEnableFedexAwbPackagingScanning(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableFedexAwbPackagingScanning: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableFedexAwbPackagingScanning)();
    }

    public getEnableInstallationPaperwork(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableInstallationPaperwork: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableInstallationPaperwork)();
    }

    public getIseCseMetricFormPowerApp(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getIseCseMetricFormPowerApp: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectIseCseMetricFormPowerApp)();
    }

    public getEnableCommercialInfoMandatory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableCommercialInfoMandatory: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableCommercialInfoMandatory)();
    }

    public getShowRemoteWork(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getShowRemoteWork: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectShowRemoteWork)();
    }

    public getShowStartImmediatelyFollowUp(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getShowStartImmediatelyFollowUp: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectShowStartImmediatelyFollowUp)();
    }

    public getEnablePhysicalInventory(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnablePhysicalInventory: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnablePhysicalInventory)();
    }

    public getEarlyTravelHours(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEarlyTravelHours: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEarlyTravelHours)();
    }

    public getRentalToolAllowWithNoConsumption(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getRentalToolAllowWithNoConsumption: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectRentalToolAllowWithNoConsumption)();
    }

    public getEnableBootstockReturn(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableBootstockReturn: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableBootstockReturn)();
    }

    public getAppBookingForPartners(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingForPartners: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingForPartners)();
    }

    public getAppBookingEnabled(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingEnabled: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingEnabled)();
    }

    public getAppBookingMinDate(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingMinDate: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingMinDate)();
    }

    public getAppBookingMaxDate(): number {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingMaxDate: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingMaxDate)();
    }

    public getAppBookingMaxDuration(): number {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingMaxDuration: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingMaxDuration)();
    }

    public getAppBookingShowDedicated(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingShowDedicated: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingShowDedicated)();
    }

    public getAppBookingDefaultDedicated(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getAppBookingDefaultDedicated: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingDefaultDedicated)();
    }

    public getAppBookingITCS(): string {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getAppBookingITCS: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectAppBookingITCS)();
    }

    public getEnableForceCheckin(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableForceCheckin: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableForceCheckin)();
    }

    public getEnableMeasurementToolsForSCRM(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMeasurementToolsForSCRM: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableMeasurementToolsForSCRM)();
    }

    public getEnableUnlimitedHandover(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableUnlimitedHandover: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableUnlimitedHandover)();
    }

    public getEnableSmartlocker(): boolean {
        if (!this.isInitialized) {
            throw new Error(`ConfigService: getEnableSmartlocker: Trying to call ConfigService before initialization!`);
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableSmartlocker)();
    }

    public getSmartlockerMaterialIdentifier(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getSmartlockerMaterialIdentifier: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectSmartlockerMaterialIdentifier)();
    }

    public getDateTimeFormatPattern(): string {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getDateTimeFormatPattern: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectDateTimeFormatPattern)();
    }

    public getUseCsmlUrlWithParam(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getUseCsmlUrlWithParam: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectUseCsmlUrlWithParam)();
    }

    public isMaterialOrderUnitNonSplittable(unit: string): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: isMaterialOrderUnitNonSplittable: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectMaterialOrderNonSplitUnits(unit))();
    }

    public getEnableMatOrderCheckOnlineOnly(): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `ConfigService: getEnableMatOrderCheckOnlineOnly: Trying to call ConfigService before initialization!`,
            );
        }
        return this.store.selectSignal(ConfigSelectors.selectEnableMatOrderCheckOnlineOnly)();
    }
}
